import { supabase } from '@/integrations/supabase/client';

export interface SuvSignConfig {
  api_url: string;
  api_key: string;
  webhook_secret: string;
  function_auth_token?: string; // JWT token for protected SuvSign edge functions
  is_active: boolean;
}

export interface SuvSignDocument {
  id: string;
  title: string;
  description?: string;
  status: string;
  file_url?: string;
  created_at: string;
  updated_at: string;
}

export interface SuvSignTemplate {
  id: string;
  name: string;
  description?: string;
}

class SuvSignService {
  private config: SuvSignConfig | null = null;

  async loadConfig(): Promise<SuvSignConfig | null> {
    const { data, error } = await supabase
      .from('suvsign_config')
      .select('*')
      .eq('is_active', true)
      .single();

    if (error || !data) {
      console.error('Error loading SuvSign config:', error);
      return null;
    }

    this.config = data;
    return data;
  }

  private async getConfig(): Promise<SuvSignConfig> {
    if (!this.config) {
      const config = await this.loadConfig();
      if (!config) {
        throw new Error('SuvSign not configured. Please configure in Settings.');
      }
      this.config = config;
    }
    return this.config;
  }

  private async makeRequest(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any
  ): Promise<any> {
    const config = await this.getConfig();
    const url = `${config.api_url}${endpoint}`;

    console.log(`🌐 SuvSign API Request: ${method} ${url}`);

    const headers: HeadersInit = {
      'X-API-Key': config.api_key,
      'Content-Type': 'application/json',
    };

    const options: RequestInit = {
      method,
      headers,
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SuvSign API Error:', errorText);
      throw new Error(`SuvSign API Error: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  async listDocuments(): Promise<SuvSignDocument[]> {
    return this.makeRequest('/documents');
  }

  async listTemplates(): Promise<SuvSignTemplate[]> {
    return this.makeRequest('/templates');
  }

  async getDocument(documentId: string): Promise<SuvSignDocument> {
    return this.makeRequest(`/documents/${documentId}`);
  }

  async createDocument(data: {
    title: string;
    description?: string;
    file_url: string;
    signatories: Array<{
      name: string;
      email: string;
    }>;
  }): Promise<SuvSignDocument> {
    return this.makeRequest('/documents', 'POST', data);
  }

  async sendForSignature(documentId: string): Promise<any> {
    return this.makeRequest(`/documents/${documentId}/send`, 'POST');
  }

  async downloadDocument(documentId: string): Promise<string> {
    const document = await this.getDocument(documentId);
    return document.file_url || '';
  }

  async cancelDocument(documentId: string): Promise<any> {
    return this.makeRequest(`/documents/${documentId}/cancel`, 'POST');
  }

  async testConnection(): Promise<boolean> {
    try {
      await this.listDocuments();
      return true;
    } catch (error) {
      console.error('Connection test failed:', error);
      throw error;
    }
  }

  // Test connection with specific config values (for testing before saving)
  async testConnectionWithConfig(testConfig: { api_url: string; api_key: string }): Promise<boolean> {
    const url = `${testConfig.api_url}/documents`;
    console.log(`🌐 SuvSign API Test Request: GET ${url}`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-API-Key': testConfig.api_key,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ SuvSign API Test Error:', errorText);
      throw new Error(`SuvSign API Error: ${response.status} ${errorText}`);
    }

    return true;
  }
}

export const suvSignService = new SuvSignService();
